import { Box, Collapse, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVoice } from '../../contexts/VoiceContext';
import {
  voiceActionsById,
  voiceExtraRemoveCountByDependencyId,
  voiceResolvedDependents,
} from '../../helpers/voiceReviewActions';
import { CommonIcon } from '../common/CommonIcon';
import { ActionCard } from './ActionCard';
import { VoiceModalAudioTranscript } from './VoiceModalAudioTranscript';

interface VoiceModalReviewViewProps {
  playbackProgress: number;
  isPlaying: boolean;
  onPlayback: () => void;
}

export const VoiceModalReviewView: FC<VoiceModalReviewViewProps> = ({
  playbackProgress,
  isPlaying,
  onPlayback,
}) => {
  const { t } = useTranslation();
  const { transcript, recordedWaveform, pendingActions, onRemoveAction } = useVoice();
  const byActionId = useMemo(() => voiceActionsById(pendingActions), [pendingActions]);
  const extraRemovesIfDeleted = useMemo(
    () => voiceExtraRemoveCountByDependencyId(pendingActions),
    [pendingActions],
  );
  const waveformBars = useMemo(
    () => (recordedWaveform.length > 0 ? recordedWaveform : Array(48).fill(0.2)),
    [recordedWaveform]
  );
  const [audioExpanded, setAudioExpanded] = useState(false);

  const hasAudioOrTranscript = waveformBars.length > 0 || transcript;

  return (
    <>
      {hasAudioOrTranscript && (
        <Box
          sx={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '14px',
            mb: 1.75,
            overflow: 'hidden',
          }}
        >
          <Box
            component="button"
            onClick={() => setAudioExpanded((e) => !e)}
            sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            p: 1.5,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--tg-theme-hint-color)',
            fontSize: 12,
            textAlign: 'left',
          }}
        >
          <Text style={{ fontSize: 12, color: 'var(--tg-theme-hint-color)' }}>
            {t('voice.audioTranscript')}
          </Text>
          <CommonIcon
            icon={audioExpanded ? 'expand_less' : 'expand_more'}
            type="filled"
            size={20}
            color="var(--tg-theme-hint-color)"
          />
        </Box>
        <Collapse in={audioExpanded}>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
            <VoiceModalAudioTranscript
              transcript={transcript}
              waveformBars={waveformBars}
              playbackProgress={playbackProgress}
              isPlaying={isPlaying}
              onPlayback={onPlayback}
            />
          </Box>
        </Collapse>
        </Box>
      )}
      <Stack gap={1} sx={{ mb: 1.75, pr: 0.25 }}>
        {pendingActions.map((a, i) => (
          <ActionCard
            key={a.id}
            action={a}
            dependentActions={voiceResolvedDependents(a.dependsOnActionIds, byActionId)}
            extraRemoveCount={extraRemovesIfDeleted.get(a.id) ?? 0}
            animDelay={i * 60}
            onRemove={onRemoveAction}
          />
        ))}
        {pendingActions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 2.5 }}>
            <Text style={{ color: 'var(--tg-theme-hint-color)', fontSize: 13 }}>
              {t('voice.noActions')}
            </Text>
          </Box>
        )}
      </Stack>
    </>
  );
};
